import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexContentBlocks, SupportedContentBlocks } from '@components/_rolex/ContentBlocks'
import RolexPage from '@components/_rolex/Page'
import { WorldOfRolexArticleIntro } from '@components/_rolex/_world-of-rolex/ArticleIntro'
import { RolexPushBanner } from '@components/_rolex/push-banner-grid/PushBanner'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { Container } from '@components/gassan-ui'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { SingleItemResponse } from '@lib/kontent'
import { getCatchAllWorldOfRolexArticlePaths } from '@lib/kontent/getCatchAllWorldOfRolexArticlePaths'
import { getPageByTypeAndUrlSlug } from '@lib/kontent/getPageByTypeAndUrlSlug'
import { RolexCollectionPage } from '@lib/kontent/models/RolexCollectionPage'
import { RolexWorldOfRolexArticle } from '@lib/kontent/models/RolexWorldOfRolexArticle'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'

type WorldOfRolexArticleProps = SingleItemResponse<RolexWorldOfRolexArticle> & {
  familyInfo?: SingleItemResponse<RolexCollectionPage>
}

const WorldOfRolexArticle: NextPage<WorldOfRolexArticleProps> = ({
  item,
  modular_content,
  familyInfo,
}) => {
  const { t } = useTranslation(['rolex', 'navigation'])
  const { locale } = useRouter()
  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  )
  const contentBlocks = item.elements.content_blocks.modular_content.map(
    (key) => modular_content[key],
  ) as SupportedContentBlocks[]

  return (
    <Page
      {...pickSeoProps(item.elements)}
      slug={`rolex${t('rolex.worldOfRolex', { ns: 'navigation' })}/${item.elements.url_slug.value}`}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="article page">
        <RolexBreadcrumbs
          items={[
            {
              label: t('navigation.worldOfRolex', { ns: 'rolex' }),
              href: `/rolex${t('rolex.worldOfRolex', { ns: 'navigation' })}`,
            },
          ]}
          currentPageTitle={item.elements.title.value}
        />
        <div className="bg-rolexLightBeige py-[60px] md:py-[90px]">
          <WorldOfRolexArticleIntro
            title={item.elements.title.value}
            description={item.elements.description.value}
            date={item.elements.publishing_date.value}
          />
          <RolexContentBlocks items={contentBlocks} />
          <Container>
            <RolexPushBanner
              mobileImage={familyInfo.item.elements.mobile_image_817de15}
              desktopImage={familyInfo.item.elements.desktop_image_2856a45}
              pretitle={`Rolex ${familyInfo.item.elements.collection_name.value}`}
              title={familyInfo.item.elements.collection_pay_off.value}
              buttonLink={`/rolex/${familyInfo.item.elements.url_slug.value}`}
              buttonText={t('cta.moreDetails')}
              gridLayout="n1_column"
            />
          </Container>
        </div>
        <KeepExploring className="py-[30px] md:py-[60px]" items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview, params }) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const urlSlug = params.slug as string
    const content = await getPageByTypeAndUrlSlug<RolexWorldOfRolexArticle>({
      language,
      type: 'rolex___world_of_rolex_article',
      urlSlug,
      preview,
    })

    let familyInfo: SingleItemResponse<RolexCollectionPage> | null
    if (content.item.elements.rolex_family.value[0]) {
      const familySlug = content.item.elements.rolex_family.value[0].codename
      familyInfo = await getPageByTypeAndUrlSlug<RolexCollectionPage>({
        type: 'rolex_collection_page',
        urlSlug: familySlug.replaceAll('_', '-'),
        language,
        preview,
      })
    }

    return {
      props: {
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
        ...content,
        familyInfo: familyInfo as SingleItemResponse<RolexCollectionPage>,
      },
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = async () => {
  if (process.env.VERCEL_ENV !== 'production') {
    return { paths: [], fallback: 'blocking' }
  }

  const paths = await getCatchAllWorldOfRolexArticlePaths()
  return {
    fallback: false,
    paths,
  }
}

export default WorldOfRolexArticle
